import { PagePath, PagePathFactory } from 'constants/pagePath';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClient } from 'urql';
import { useAuthHandler } from 'views/auth/useAuth';
import { AccountType } from 'views/utils/enums';
import { ProductType } from 'constants/productType';
import { AccountFragment, useGetIssuanceDocumentFolderListQuery } from 'views/utils/graphql/graphql';

type SingleMainMenuItem = {
  key: string;
  name: string;
  onClick: () => void;
};

export type MainMenuItemProps = SingleMainMenuItem & {
  childItems?: Array<SingleMainMenuItem>;
};

export type MainMenuProps = {
  account: AccountFragment;
};

export type MenuItems = {
  account: { branch: string; lastLoginDateTime: string };
  notice: SingleMainMenuItem;
  faq: MainMenuItemProps | null;
  contact: SingleMainMenuItem;
  logout: SingleMainMenuItem;
  items: Array<MainMenuItemProps>;
};

export const useMainMenu = (props: MainMenuProps) => {
  const navigate = useNavigate();
  const client = useClient();
  const { logout } = useAuthHandler();
  const [documentFolderList] = useGetIssuanceDocumentFolderListQuery({
    variables: {
      count: 100,
      page: 1,
    },
    requestPolicy: 'network-only',
  });

  return useMemo<MenuItems>(() => {
    return {
      account: {
        branch: props.account.Branch || '',
        lastLoginDateTime: props.account.LastLoginDateTime || '',
      },
      notice: { key: 'notice', name: 'お知らせ', onClick: () => navigate(PagePath.Notice) },
      faq: props.account.AccountType === AccountType.Corporate ? 
      { 
        key: 'faq', 
        name: 'FAQ', 
        onClick: () => navigate(PagePathFactory.faqSymbolProductType(ProductType.LeaseFund)),
        childItems:[
          {key: 'faqLeaseFund', name: 'リースファンド', onClick: () => navigate(PagePathFactory.faqSymbolProductType(ProductType.LeaseFund))},
          {key: 'faqOverseas', name: '海外不動産', onClick: () => navigate(PagePathFactory.faqSymbolProductType(ProductType.OverseasRealEstate))}
        ]
      } : null,
      contact: {
        key: 'contact',
        name: 'お問い合わせ',
        onClick: () => navigate(PagePath.Contact),
      },
      logout: { key: 'logout', name: 'ログアウト', onClick: () => logout(client) },
      items: [
        { key: 'home', name: 'HOME', onClick: () => navigate(PagePath.Home) },
        {
          key: 'programList',
          name: '案件一覧',
          onClick: () => navigate(PagePath.ProgramList),
        },
        { key: 'position', name: '出資案件一覧', onClick: () => navigate(PagePath.Position) },
        { key: 'requestHistory', name: '申込履歴', onClick: () => navigate(PagePath.RequestHistory) },
        {
          key: 'issuanceDocument',
          name: '交付書面',
          onClick: () => navigate(PagePath.IssuanceDocument),
          childItems:
            documentFolderList.data?.GetIssuanceDocumentFolderList?.Data?.filter((x) => !!x).map((folder) => ({
              key: folder!.ID,
              name: folder!.Name,
              onClick: () => navigate(PagePathFactory.issuanceDocumentFromDocumentType(folder!.ID)),
            })) || [],
        },
        {
          key: 'accountInfo',
          name: '登録情報',
          onClick: () => navigate(PagePath.AccountInfo),
          childItems: [
            { key: 'accountInfo', name: 'ご登録内容の照会/変更', onClick: () => navigate(PagePath.AccountInfo) },
            {
              key: 'compatibilityCheck',
              name: '適合性チェック回答入力',
              onClick: () => navigate(PagePath.CompatibilityCheck),
            },
            {
              key: 'confirmCompatibilityCheck',
              name: '適合性チェック結果確認',
              onClick: () => navigate(PagePath.ConfirmCompatibilityCheck),
            },
            ...(props.account.AccountType === AccountType.Corporate
              ? [{ key: 'subUser', name: '利用者登録', onClick: () => navigate(PagePath.SubUser) }]
              : []),
            { key: 'changePassword', name: 'パスワード変更', onClick: () => navigate(PagePath.ChangePassword) },
          ],
        },
      ],
    };
  }, [props.account, documentFolderList.data?.GetIssuanceDocumentFolderList?.Data]);
};
